/**
 * API Service - Axios instance with JWT auth interceptor
 * Base URL proxied via CRA proxy in dev, direct in prod
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach JWT
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle 401 and refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const resp = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token: newRefresh } = resp.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefresh);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// --- Auth ---
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  ms365Login: () => {
    // Full-page redirect must hit backend directly — CRA proxy only handles XHR
    const base = BACKEND_URL || window.location.origin;
    window.location.href = `${base}/api/auth/ms365/login`;
  },
  devLogin: (data) => api.post('/auth/dev-login', data),
  staffLogin: (email, password) => api.post('/auth/staff-login', { email, password }),
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  getProfile: () => api.get('/auth/me'),
  createClientUser: (data) => api.post('/auth/client-users', data),
  updateClientUser: (id, data) => api.put(`/auth/client-users/${id}`, data),
  deactivateClientUser: (id) => api.delete(`/auth/client-users/${id}`),
  changePassword: (data) => api.post('/auth/change-password', data),

};

// --- Tickets ---
export const ticketAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  // Workflow actions
  triage: (id, data) => api.post(`/tickets/${id}/triage`, data),
  assignGroup: (id, data) => api.post(`/tickets/${id}/assign-group`, data),
  assignIndividual: (id, data) => api.post(`/tickets/${id}/assign-individual`, data),
  commenceResolution: (id, data) => api.post(`/tickets/${id}/commence-resolution`, data),
  completeResolution: (id, data) => api.post(`/tickets/${id}/complete-resolution`, data),
  verify: (id, data) => api.post(`/tickets/${id}/verify`, data),
  close: (id, data) => api.post(`/tickets/${id}/close`, data),
  reject: (id, data) => api.post(`/tickets/${id}/reject`, data),
  // Audit & attachments
  getAudit: (id) => api.get(`/tickets/${id}/audit`),
  getAttachments: (id) => api.get(`/tickets/${id}/attachments`),
  uploadAttachment: (id, file, stage = 'CREATION') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tickets/${id}/attachments?stage=${stage}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDownloadUrl: (ticketId, attachmentId) =>
    api.get(`/tickets/${ticketId}/attachments/${attachmentId}/download`),
  deleteAttachment: (ticketId, attachmentId) =>
    api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),
  withdraw: (id, data) => api.post(`/tickets/${id}/withdraw`, data || {}),
  reassign: (id, data) => api.post(`/tickets/${id}/reassign`, data),
};

// --- Metadata ---
export const metadataAPI = {
  getIssueTypes: () => api.get('/metadata/issue-types'),
  getImpactLevels: () => api.get('/metadata/impact-levels'),
  getSeverityLevels: () => api.get('/metadata/severity-levels'),
  getStatuses: () => api.get('/metadata/statuses'),
  getFeatures: () => api.get('/metadata/features'),
  getClients: () => api.get('/metadata/clients'),
  getClientModules: (clientId) => api.get(`/metadata/clients/${clientId}/modules`),
  getClientMarts: (clientId) => api.get(`/metadata/clients/${clientId}/marts`),
  getClientUsers: (clientId, includeInactive = false) =>
    api.get(`/metadata/clients/${clientId}/users`, { params: includeInactive ? { include_inactive: true } : {} }),
  getClientHolidays: (clientId) => api.get(`/metadata/holidays/${clientId}`),
  getAxslogicHolidays: () => api.get('/metadata/holidays/axslogic'),
  getGroups: () => api.get('/metadata/groups'),
};

// --- Admin ---
export const adminAPI = {
  // Issue types
  getIssueTypes: () => api.get('/admin/issue-types'),
  createIssueType: (data) => api.post('/admin/issue-types', data),
  updateIssueType: (id, data) => api.put(`/admin/issue-types/${id}`, data),
  deleteIssueType: (id) => api.delete(`/admin/issue-types/${id}`),
  // Features
  getFeatures: () => api.get('/admin/features'),
  createFeature: (data) => api.post('/admin/features', data),
  updateFeature: (id, data) => api.put(`/admin/features/${id}`, data),
  // Client SLA
  getClientSLA: (clientId) => api.get(`/admin/clients/${clientId}/sla`),
  upsertClientSLA: (clientId, data) => api.post(`/admin/clients/${clientId}/sla`, data),
  seedDefaultSLA: (clientId) => api.post(`/admin/clients/${clientId}/sla/seed-defaults`),
  // Escalation
  getEscalation: (clientId) => api.get(`/admin/clients/${clientId}/escalation`),
  upsertEscalation: (clientId, data) => api.post(`/admin/clients/${clientId}/escalation`, data),
  // Role mappings
  getRoleMappings: (clientId) => api.get(`/admin/clients/${clientId}/role-mappings`),
  upsertRoleMapping: (clientId, data) => api.post(`/admin/clients/${clientId}/role-mappings`, data),
  // Assignment rules
  getAssignmentRules: () => api.get('/admin/assignment-rules'),
  upsertAssignmentRule: (data) => api.post('/admin/assignment-rules', data),
  triggerHubSync: () => api.post('/admin/hub-sync/trigger'),
  getHubSyncStatus: () => api.get('/admin/hub-sync/status'),
  getDashboardTelemetry: (clientId) =>
      api.get('/admin/dashboard/telemetry', { params: clientId ? { client_id: clientId } : {} }),
  getClientBreakdown: () => api.get('/admin/dashboard/client-breakdown'),
  deleteFeature: (id) => api.delete(`/admin/features/${id}`),
};

export default api;
